import { Button, IconButton, Stack, Typography } from "@mui/material";
import { TextFieldElement as TextField } from "react-hook-form-mui";
import { useTheme } from "@mui/material/styles";
import SendIcon from "@mui/icons-material/Send";
import { useRef, useEffect } from "react";
import { RefineListView } from "../refine-list-view";
import { ChatBubble } from "./ChatBubble";
import { IBroadcastChat } from "@/utils/zod/broadcastChatSchema";
import { ListMessage, Message } from "@/utils/zod/messageSchema";
import { useInvalidate, useCustomMutation, useModal, useCustom } from "@refinedev/core";
import { DateField, DeleteButton, EditButton } from "@refinedev/mui";
import { Add as AddIcon } from "@mui/icons-material";
import { MediaDialog } from "../medias/MediaDialog";
import { useForm } from "@refinedev/react-hook-form";
import { FormProvider } from "react-hook-form";
import { BroadcastSetting } from "@/utils/zod/broadcastRoomSchema";
import { IMedia } from "@/interfaces";

// Add this new component for date separator
const DateSeparator = ({ date }: { date: string }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{ my: 2 }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 2,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[200],
          color: isDarkMode ? theme.palette.grey[400] : theme.palette.grey[600],
        }}
      >
        <DateField
          value={date}
          format="DD MMM YYYY"
        />
      </Typography>
    </Stack>
  );
};

export const ChatWindow = ({ chatId, onChatDeleted }: { chatId: string, onChatDeleted?: () => void }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const invalidate = useInvalidate();
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { data: selectedChat } = useCustom<IBroadcastChat>({
    url: `/rooms/broadcasts/${chatId}`,
    method: "get"
  });

  const { show, close, visible } = useModal();

  const { mutateAsync: sendMessage } = useCustomMutation<ListMessage>()

  const form = useForm<{
    "message": string;
    "broadcast-medias": IMedia[];
    "broadcast_setting": BroadcastSetting;
  }>({
    defaultValues: {
      "message": "",
      "broadcast-medias": [],
      "broadcast_setting": {} as BroadcastSetting
    },
  });

  // Only set the value if broadcast_setting exists
  useEffect(() => {
    if (selectedChat?.data?.broadcast_setting) {
      form.setValue("broadcast_setting", selectedChat.data.broadcast_setting);
    }
  }, [selectedChat?.data?.broadcast_setting]);

  const handleSendMessage = async (message: Message) => {
    if (form.getValues("message").trim() || form.getValues("broadcast-medias").length) {

      try {
        const response = await sendMessage({
          url: "/rooms/broadcasts/messages",
          method: "post",
          values: message,
        });
        form.setValue("message", "");
        form.setValue("broadcast-medias", []);

        // Update the messages list in the current view
        if (selectedChat?.data) {
          selectedChat.data.messages = [...(selectedChat.data.messages || []), response.data];
        }

        await invalidate({
          invalidates: ["list"],
          resource: "rooms/broadcasts",
        });

        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }

      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  };

  if (!selectedChat) return null;

  return (
    <FormProvider {...form}>
      <RefineListView
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6">{selectedChat.data.name}</Typography>
          </Stack>
        }
        wrapperProps={{ sx: { height: "100%", border: `1px solid ${theme.palette.divider}`, borderRadius: 0, borderLeft: "none" } }}
        headerButtons={() => [
          <EditButton
            key="edit"
            size="medium"
            variant="text"
            resource="rooms/broadcasts"
            recordItemId={chatId}
            hideText
          />,
          <DeleteButton
            key="delete"
            size="medium"
            variant="text"
            resource="rooms/broadcasts"
            recordItemId={chatId}
            hideText
            onSuccess={() => {
              onChatDeleted?.();
            }}
          />
        ]}
        headerProps={{
          sx: {
            height: 84,
            paddingInline: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }
        }}
        contentProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            height: "calc(100% - 84px)", // Account for header height
            padding: 0,
          }
        }}
      >
        {/* Messages Area */}
        <Stack
          ref={messagesContainerRef}
          direction="column"
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: 2,
            paddingBottom: 0,
            backgroundColor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        >
          <Stack spacing={0}>
            {selectedChat.data.messages?.map((message, index) => {
              const currentDate = new Date(message.server_received_at).toDateString();
              const nextMessage = selectedChat.data.messages?.[index - 1];
              const nextDate = nextMessage
                ? new Date(nextMessage.server_received_at).toDateString()
                : null;
              const isFirstMessage = index === 0;

              return (
                <Stack key={index}>
                  {(isFirstMessage || (nextDate && currentDate !== nextDate)) && (
                    <DateSeparator date={message.server_received_at} />
                  )}
                  <ChatBubble message={message} />
                </Stack>
              );
            })}
          </Stack>
        </Stack>

        {/* Message Input */}
        <Stack direction="column" spacing={1} sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          padding: 1,
          paddingBottom: 0,
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <IconButton
              onClick={show}
            >
              <AddIcon fontSize="small" color="primary" />
            </IconButton>
            <Typography variant="subtitle1" color={theme.palette.warning.main} fontWeight="bold">
              Current sender: {selectedChat.data.broadcast_setting.sender_account.name ?? "Unknown"}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              backgroundColor: "background.paper",
            }}
          >
            <TextField
              multiline={true}
              name="message"
              minRows={1}
              fullWidth
              variant="outlined"
              placeholder="Type a message"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage({
                    format: "text",
                    origin: "broadcast::manual",
                    room_id: selectedChat.data.id,
                    text: form.getValues("message"),
                    type: "public",
                    inserted_at: new Date().toISOString(),
                    account_type: "seller",
                    account_id: selectedChat.data.broadcast_setting.sender_account.id,
                  });
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSendMessage({
                format: "text",
                origin: "broadcast::manual",
                room_id: selectedChat.data.id,
                text: form.getValues("message"),
                type: "public",
                inserted_at: new Date().toISOString(),
                account_type: "seller",
                account_id: selectedChat.data.broadcast_setting.sender_account.id,
              })}
              endIcon={<SendIcon />}
              disabled={!form.formState.isDirty || !form.getValues("message").trim()} // Efficient tracking
              sx={{
                height: 'fit-content',
              }}
            >
              Send
            </Button>
          </Stack>
        </Stack>
      </RefineListView>
      <MediaDialog
        open={visible}
        onClose={close}
        name="broadcast-medias"
        onAdd={(medias) => {
          // Update form state with selected media
          form.setValue("broadcast-medias", medias, { shouldDirty: true });
          
          // Send each media as a message
          medias.forEach((media) => {
            handleSendMessage({
              format: media.type as "image" | "video",
              origin: "broadcast::manual",
              room_id: selectedChat.data.id,
              type: "public",
              cdn_url: media.cdn_url,
              inserted_at: new Date().toISOString(),
              account_type: "seller",
              account_id: selectedChat.data.broadcast_setting.sender_account.id,
            });
          });
        }}
      />
    </FormProvider>
  );
}; 